<template>
  <div>
    <b-card
      no-body
      class="liveinfo-form"
    >
      <div class="m-2">
        <b-row>
          <b-col
            cols="12"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <b-form-input
              v-model="query.liveInfoId"
              class="el-def"
              placeholder="ID"
              style="width: 100px;"
            />
            <v-select
              v-model="query.clientId"
              :options="codes.clients"
              placeholder="제약사"
              :reduce="option => option.code"
              class="el-def"
              style="width: 200px;"
            />
            <b-form-input
              v-model="query.productName"
              class="el-def"
              placeholder="제품명"
              style="width: 180px;"
            />
            <b-form-datepicker
              v-model="query.searchStartDate"
              class="el-def"
              placeholder="게시기간(Fr)"
              style="width: 230px;"
            />
            <label class="ml-25 mr-50">~</label>
            <b-form-datepicker
              v-model="query.searchEndDate"
              class="el-def"
              placeholder="게시기간(To)"
              style="width: 230px;"
            />
            <v-select
              v-model="query.postStatus"
              :options="codes.postStatusType"
              class="invoice-filter-select el-def"
              :reduce="option => option.code"
              placeholder="게시상태"
              style="width: 160px;"
            />
            <v-select
              v-model="query.patientTypeStatus"
              :options="codes.patientTypeStatus"
              class="invoice-filter-select el-def"
              :reduce="option => option.code"
              placeholder="진행상태"
              style="width: 160px;"
            />
            <b-button
              variant="primary"
              class="btn-search"
              @click.prevent="refreshPatientTypeList()"
            >
              조회
            </b-button>
          </b-col>
        </b-row>
      </div>
    </b-card>

    <b-card no-body>
      <div class="m-2">
        <b-row>
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start"
          >
            <h5 class="mb-0 ml-50">
              환자 타입 조회 리스트 [{{ totalRecords }}] 건
            </h5>
          </b-col>
        </b-row>
      </div>
      <b-table
        ref="refPatientTypeTable"
        primary-key="liveInfoId"
        :items="fetchPatientTypeList"
        :fields="tableColumns"
        :sort-by.sync="sortBy"
        :sort-desc.sync="isSortDesc"
        responsive
        sticky-header
        selectable
        select-mode="single"
        class="position-relative"
        empty-text="일치하는 데이터가 없습니다."
        show-empty
        @row-selected="onRowSelected"
      >
        <template #cell(postPeriod)="data">
          <span class="text-nowrap">
            {{ data.item.postStartDate | $dateFormatter('YYYY-MM-DD') }} ~ {{ data.item.postEndDate | $dateFormatter('YYYY-MM-DD') }}
          </span>
        </template>
        <template #cell(postStatus)="data">
          <div class="text-nowrap" style="text-align: center;">
            {{ formatPostStatus(data.value) }}
          </div>
        </template>
        <template #cell(patientTypeStatus)="data">
          <div class="text-nowrap" style="text-align: center;">
            {{ formatPatientTypeStatus(data.item) }}
          </div>
        </template>
        <template #cell(typeCount)="data">
          <div class="text-nowrap" style="text-align: center;">
            {{ data.value }}
          </div>
        </template>
      </b-table>
      <b-row>
        <b-col cols="12">
          <b-pagination
            v-model="currPage"
            :total-rows="totalRecords"
            :per-page="pageCount"
            limit="10"
            align="center"
          />
        </b-col>
      </b-row>
    </b-card>

    <b-card
      no-body
      class="liveinfo-form"
    >
      <div class="m-2">
        <b-row>
          <b-col
            md="9"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <b-form-datepicker
              v-model="excelQuery.startDate"
              class="el-def"
              placeholder="조회기간(Fr)"
              style="width: 230px;"
            />
            <label class="ml-25 mr-50">~</label>
            <b-form-datepicker
              v-model="excelQuery.endDate"
              class="el-def"
              placeholder="조회기간(To)"
              style="width: 230px;"
            />
            <label style="width: 160px">(*당일데이터 조회 불가)</label>
          </b-col>
          <b-col
            md="3"
            class="d-flex align-items-center justify-content-end"
          >
            <b-button
              variant="outline-dark"
              class="btn-search ml-1"
              :disabled="!selectedItem"
              @click.prevent="excelDownload()"
            >
              <feather-icon
                icon="DownloadIcon"
                class="mr-50"
              />
              <span>엑셀 다운로드</span>
            </b-button>
          </b-col>
        </b-row>
        <b-row class="mt-1">
          <b-col cols="12" class="ml-50">
            <span>
              {{ selectedItem ? '해당 통계는 엑셀 다운로드만 제공합니다.' : '환자 타입 조회를 선택해 주세요.' }}
            </span>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import axios from '@axios'
import { onMounted, ref, watch } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import dayjs from 'dayjs'
import vSelect from 'vue-select'
import { saveAs } from 'file-saver'

export default {
  components: {
    vSelect,
  },

  setup() {
    onMounted(() => {
      fetchCodes()
    })

    const toast = useToast()
    const pushToast = (variant, title) => {
      let icon

      if (variant === 'success') {
        icon = 'CheckCircleIcon'
      } else {
        icon = 'AlertTriangleIcon'
      }

      toast({
        component: ToastificationContent,
        props: { title, icon, variant },
      })
    }

    // Set Codes
    const codes = ref({
      clients: [],
      postStatusType: [],
      patientTypeStatus: [],
    })

    const fetchCodes = () => {
      axios.get('/fa/druginfo/codes')
        .then(rs => {
          const { clients, postStatusType, patientTypeStatus } = rs.data

          codes.value.clients = clients
          codes.value.postStatusType = postStatusType
          codes.value.patientTypeStatus = patientTypeStatus
        })
        .catch(() => {
          pushToast('danger', '코드 데이터를 불러오는데 실패하였습니다.')
        })
    }
    // Set Codes End.

    // Main Table Config
    const refPatientTypeTable = ref(null)
    const sortBy = ref('liveInfoId')
    const isSortDesc = ref(true)
    const currPage = ref(1)
    const pageCount = ref(10)
    const totalRecords = ref(0)
    const tableColumns = [
      {
        key: 'liveInfoId', label: 'ID', sortable: true, thStyle: { width: '8%' },
      },
      {
        key: 'clientName', label: '제약사명', sortable: true, thStyle: { width: '15%' },
      },
      {
        key: 'productName', label: '제품명', sortable: true, thStyle: { width: '15%' },
      },
      {
        key: 'postPeriod', label: '게시 기간', sortable: true, thStyle: { width: '20%' },
      },
      {
        key: 'postStatus', label: '게시 상태', sortable: true, thStyle: { width: '12%' },
      },
      {
        key: 'patientTypeStatus', label: '진행 상태(회차)', sortable: true, thStyle: { width: '15%' },
      },
      {
        key: 'typeCount', label: 'Type 수', sortable: true, thStyle: { width: '8%' },
      },
    ]
    const fieldMap = {
      postPeriod: 'PostStartDate',
    }
    watch([currPage], () => {
      refreshPatientTypeList()
    })
    // Main Table Config End.

    const refreshPatientTypeList = () => {
      refPatientTypeTable.value.refresh()
    }

    const query = ref({
      liveInfoId: null,
      clientId: null,
      productName: null,
      searchStartDate: null,
      searchEndDate: null,
      postStatus: null,
      patientTypeStatus: null,
    })

    const fetchPatientTypeList = (ctx, callback) => {
      const frDate = dayjs(query.value.searchStartDate)
      const toDate = dayjs(query.value.searchEndDate)

      if (query.value.searchStartDate && query.value.searchEndDate && toDate.diff(frDate, 'day') < 0) {
        pushToast('warning', '종료일이 시작일보다 앞설 수 없습니다.')
        callback([])
        return
      }

      axios.post('/fa/statistics/patient-type/list', {
        search: {
          query: query.value,
        },
        sort: {
          predicate: fieldMap[sortBy.value] || sortBy.value,
          reverse: isSortDesc.value,
        },
        pagination: {
          number: currPage.value,
          count: pageCount.value,
        },
      })
        .then(rs => {
          const { items, totalRecord } = rs.data
          callback(items)
          totalRecords.value = totalRecord
        })
        .catch(err => {
          pushToast('danger', `데이터 조회 중 오류가 발생하였습니다.${err}`)
          callback([])
        })
    }

    // Row Select & Excel Query
    const yesterday = dayjs().add(-1, 'day').format('YYYY-MM-DD')
    const maxExcelDate = yesterday

    const excelQuery = ref({
      startDate: dayjs().add(-1, 'month').add(-1, 'day').format('YYYY-MM-DD'),
      endDate: yesterday,
    })

    const selectedItem = ref(null)

    const onRowSelected = items => {
      const item = items[0] ?? null
      selectedItem.value = item

      if (item) {
        // 게시기간으로 자동 세팅 (미래 일자는 어제까지로 클램프)
        const postStart = dayjs(item.postStartDate).format('YYYY-MM-DD')
        const postEnd = dayjs(item.postEndDate).format('YYYY-MM-DD')

        excelQuery.value.startDate = postStart > yesterday ? yesterday : postStart
        excelQuery.value.endDate = postEnd > yesterday ? yesterday : postEnd
      } else {
        excelQuery.value.startDate = dayjs().add(-1, 'month').add(-1, 'day').format('YYYY-MM-DD')
        excelQuery.value.endDate = yesterday
      }
    }

    // 게시/진행 상태 표기
    const formatPostStatus = code => {
      const opt = codes.value.postStatusType.find(o => o.code === code)
      return opt ? opt.label : ''
    }

    const formatPatientTypeStatus = item => {
      const opt = codes.value.patientTypeStatus.find(o => o.code === item.patientTypeStatus)
      if (!opt) return ''
      const currentRound = item.currentRound ?? 0
      const totalRound = item.totalRound ?? 0
      return `${opt.label}(${currentRound}/${totalRound}회차)`
    }

    // Excel Download
    const excelDownload = () => {
      if (!selectedItem.value) {
        pushToast('warning', '환자 타입 조회를 선택해 주세요.')
        return
      }

      const frDate = dayjs(excelQuery.value.startDate)
      const toDate = dayjs(excelQuery.value.endDate)

      if (toDate.diff(frDate, 'day') < 0) {
        pushToast('warning', '종료일이 시작일보다 앞설 수 없습니다.')
        return
      }

      // const today = dayjs().format('YYYY-MM-DD')
      // if (excelQuery.value.startDate >= today || excelQuery.value.endDate >= today) {
      //   pushToast('warning', '당일 데이터는 조회할 수 없습니다.')
      //   return
      // }

      axios.get('/fa/statistics/patient-type/excel', {
        params: {
          liveInfoId: selectedItem.value.liveInfoId,
          startDate: excelQuery.value.startDate,
          endDate: excelQuery.value.endDate,
        },
        responseType: 'blob',
      })
        .then(rs => {
          if (rs?.data?.size > 0) {
            const blob = new Blob([rs.data])

            saveAs(blob, `${selectedItem.value.liveInfoId}__(${dayjs(excelQuery.value.startDate).format('YYMMDD')}-${dayjs(excelQuery.value.endDate).format('YYMMDD')})__환자타입조회 통계.xlsx`)
          }
        })
        .catch(err => {
          pushToast('danger', `데이터 조회 중 오류가 발생하였습니다.${err}`)
        })
    }

    return {
      codes,
      query,
      refPatientTypeTable,
      sortBy,
      isSortDesc,
      currPage,
      pageCount,
      totalRecords,
      tableColumns,
      fetchPatientTypeList,
      refreshPatientTypeList,
      selectedItem,
      excelQuery,
      maxExcelDate,
      onRowSelected,
      formatPostStatus,
      formatPatientTypeStatus,
      excelDownload,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

.dropdown-menu {
  z-index: 100 !important;
}
</style>
